import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Navigation, Square, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Cleaner-side live trip control. Starts a browser Geolocation.watchPosition,
// sends POST /trip/ping every ~30s. The backend auto-stops the trip when the
// job starts/completes/cancels, so this component also polls GET /trip once
// per minute to detect server-side stop and tear itself down.
const PING_INTERVAL_MS = 30000;

export default function CleanerTripControls({ job, onChanged }) {
  const [active, setActive] = useState(!!job.trip_active);
  const [permState, setPermState] = useState("unknown"); // unknown|granted|denied|unavailable
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const watchIdRef = useRef(null);
  const sendTimerRef = useRef(null);
  const lastPosRef = useRef(null);
  const pollTimerRef = useRef(null);

  const serverEndTrip = async () => {
    try { await api.post(`/jobs/${job.id}/trip/stop`); } catch (_) { /* best-effort */ }
  };

  const teardown = (opts = {}) => {
    if (watchIdRef.current != null && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_) {}
      watchIdRef.current = null;
    }
    if (sendTimerRef.current) { clearInterval(sendTimerRef.current); sendTimerRef.current = null; }
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    lastPosRef.current = null;
    setActive(false);
    if (opts.callServer) serverEndTrip();
    if (onChanged) onChanged();
  };

  const sendPing = async () => {
    if (!lastPosRef.current) return;
    const { lat, lng, accuracy, speed } = lastPosRef.current;
    try {
      await api.post(`/jobs/${job.id}/trip/ping`, { lat, lng, accuracy, speed });
    } catch (e) {
      // 400 "Trip ended" → server auto-stopped, tear down locally too.
      if (e?.response?.status === 400) teardown();
    }
  };

  const startWatcher = () => {
    if (!navigator.geolocation) {
      setPermState("unavailable");
      toast.warning("Location unavailable on this device — customer will see 'Live location unavailable'. Trip is still active.");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPermState("granted");
        lastPosRef.current = {
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy, speed: pos.coords.speed,
        };
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setPermState("denied");
          toast.warning("Location permission denied — customer will see 'Live location unavailable'. Trip is still active and the job will work normally.");
        } else {
          setPermState("unavailable");
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
    // First ping as soon as we have a fix, then every PING_INTERVAL_MS.
    sendTimerRef.current = setInterval(sendPing, PING_INTERVAL_MS);
  };

  const startTrip = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/trip/start`);
      setActive(true);
      setConfirmOpen(false);
      toast.success("Trip started — customer can see your approximate location");
      startWatcher();
      // Poll server state every 60s so admin-side cancel/reassign also tears us down.
      pollTimerRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/jobs/${job.id}/trip`);
          if (!data.trip_active) teardown();
        } catch (_) {}
      }, 60000);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start trip");
    }
    setBusy(false);
  };

  const stopTrip = async () => {
    setBusy(true);
    teardown({ callServer: true });
    setBusy(false);
    toast.success("Trip ended");
  };

  // Resume watcher if the user reloads while trip is still active server-side.
  useEffect(() => {
    if (job.trip_active && !watchIdRef.current) {
      setActive(true);
      startWatcher();
      pollTimerRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/jobs/${job.id}/trip`);
          if (!data.trip_active) teardown();
        } catch (_) {}
      }, 60000);
    }
    return () => teardown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (active) {
    const permBadge = {
      granted:     { text: "Location sharing active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
      denied:      { text: "Permission denied · customer sees 'Live location unavailable'", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
      unavailable: { text: "Location unavailable on this device", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
      unknown:     { text: "Requesting location permission…", cls: "bg-slate-50 text-slate-700 border-slate-200", Icon: Loader2 },
    }[permState];
    const PI = permBadge.Icon;
    return (
      <div className="flex flex-col gap-1" data-testid={`trip-active-${job.id}`}>
        <div className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border px-2 py-1 rounded ${permBadge.cls}`}>
          <PI className={`w-3 h-3 ${permState === "unknown" ? "animate-spin" : ""}`} />
          {permBadge.text}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={stopTrip}
          disabled={busy}
          data-testid={`trip-stop-${job.id}`}
          className="border-slate-300 text-slate-700"
        >
          <Square className="w-3.5 h-3.5 mr-1" />End trip
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        size="sm"
        onClick={() => setConfirmOpen(true)}
        disabled={busy || job.status !== "matched"}
        data-testid={`trip-start-${job.id}`}
        className="bg-teal-600 hover:bg-teal-700 text-white"
      >
        <Navigation className="w-3.5 h-3.5 mr-1" />Start Trip
      </Button>
      <Dialog open={confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(false)}>
        <DialogContent className="bg-white max-w-sm" data-testid="trip-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display">Share your location?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700 space-y-2 py-2">
            <p>Your approximate location will be shared <b>only with this booking's customer</b> while you travel. Sharing automatically stops when you arrive, complete, or cancel the job.</p>
            <p className="text-xs text-slate-500">The customer never sees your exact address — your position is rounded on the server before anyone sees it.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="trip-confirm-back">Not now</Button>
            <Button onClick={startTrip} disabled={busy} data-testid="trip-confirm-start" className="bg-teal-600 text-white">
              {busy ? "Starting…" : "Start trip"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
